/**
 * Client store for the Order History screen.
 *
 * Holds the order rows and the list's UI state (search text, page, page size,
 * the two facets, the date window, which overlay is open). Rows arrive from
 * `GET orders/` through the SSR fetcher and are hydrated here by
 * OrdersInitializer — the slice itself never fetches, and the table renders the
 * rows exactly as given (search, filtering and paging are answered server-side
 * via OrdersQuerySync).
 *
 * Two shapes here follow the API rather than the design:
 *
 * · **`status` is ONE field, not two.** The segmented tabs and the Filter
 *   popover's status select both write it, because the API has one `?status=`
 *   parameter. Treating them as separate facets and AND-ing them client-side
 *   would filter a page the server already narrowed.
 * · **`period` and the explicit range are mutually exclusive** — sending both
 *   is a 400 — so choosing a preset clears the range and vice versa. Only an
 *   explicit range produces a `revenue_delta` on the KPI band.
 */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import {
  DEFAULT_ORDER_PAGE_SIZE,
  DEFAULT_ORDER_PERIOD,
} from "@/actions/ssr/orderHistory/ordersApi";
import type { OrderPage, OrderRecord } from "@/components/order-history/types";

/** Which overlay is on screen. Only one can be open, so one discriminator. */
export type OrderOverlay = "details" | "refund" | "cancel";

/**
 * The two facets the Filter popover applies. Empty string means "no filter",
 * which keeps each a plain single-select.
 *
 * `status` accepts any of the **nine** `form-options/` values — six commercial,
 * three kitchen — so it is a string, not the six-value `OrderStatus` union.
 */
export interface OrderFilters {
  status: string;
  channel: string;
}

export const EMPTY_ORDER_FILTERS: OrderFilters = { status: "", channel: "" };

/**
 * The window the whole screen reads through — the table, the KPI cards and the
 * export all take it.
 *
 * `period` is a server-resolved preset token; `dateFrom`/`dateTo` are an
 * explicit trading-day range. Never both.
 */
export interface OrderDateWindow {
  period: string;
  dateFrom: string;
  dateTo: string;
}

/** The screen opens on today's trading day, matching the header's idle label. */
export const DEFAULT_ORDER_WINDOW: OrderDateWindow = {
  period: DEFAULT_ORDER_PERIOD,
  dateFrom: "",
  dateTo: "",
};

export interface OrdersState {
  orders: OrderRecord[];
  /** Total rows across all pages — drives "1-8 of 180". */
  total: number;
  page: number;
  pageSize: number;
  search: string;
  filters: OrderFilters;
  window: OrderDateWindow;
  /** Open overlay, or null when the list is uncovered. */
  overlay: OrderOverlay | null;
  /** Order the open overlay acts on. */
  activeOrderId: string | null;
  /** A list navigation is in flight — see `setOrdersListPending`. */
  listPending: boolean;
}

const initialState: OrdersState = {
  orders: [],
  total: 0,
  page: 1,
  pageSize: DEFAULT_ORDER_PAGE_SIZE,
  search: "",
  filters: EMPTY_ORDER_FILTERS,
  window: DEFAULT_ORDER_WINDOW,
  overlay: null,
  activeOrderId: null,
  listPending: false,
};

const ordersSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    /**
     * True from the moment a query change starts navigating until the server
     * render lands. Set by `OrdersQuerySync`, which owns the transition; read
     * by the table, which is a sibling and cannot see it any other way.
     */
    setOrdersListPending(state, action: PayloadAction<boolean>) {
      state.listPending = action.payload;
    },
    /**
     * Seed the list's UI state from the query the server actually rendered.
     *
     * **Called once, on first mount only.** Without it a deep link or a reload
     * carrying `?status=completed` would paint the right rows and then have
     * OrdersQuerySync — reading a slice that still holds its defaults —
     * immediately rewrite the URL back to nothing and refetch, wiping the
     * filter the user had shared. Re-running it on later navigations is what
     * must NOT happen: a server response for "abc" landing while the user has
     * typed "abcd" would clobber the newer keystrokes.
     */
    hydrateOrdersQuery(
      state,
      action: PayloadAction<{
        page: number;
        pageSize: number;
        search: string;
        filters: OrderFilters;
        window: OrderDateWindow;
      }>,
    ) {
      state.page = action.payload.page;
      state.pageSize = action.payload.pageSize;
      state.search = action.payload.search;
      state.filters = action.payload.filters;
      state.window = action.payload.window;
    },
    /** Replace the list with a server-rendered page (called by the initializer). */
    setOrders(state, action: PayloadAction<OrderPage>) {
      state.orders = action.payload.orders;
      state.total = action.payload.total;
      state.page = action.payload.page;
      state.pageSize = action.payload.pageSize;
    },
    setOrdersPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },
    setOrdersPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
      // A larger page size can put the current page out of range
      state.page = 1;
    },
    setOrdersSearch(state, action: PayloadAction<string>) {
      state.search = action.payload;
      // Any new query restarts paging from the first page
      state.page = 1;
    },
    /** The one status field both the tabs and the Filter popover write. */
    setOrdersStatus(state, action: PayloadAction<string>) {
      state.filters.status = action.payload;
      state.page = 1;
    },
    setOrdersFilters(state, action: PayloadAction<OrderFilters>) {
      state.filters = action.payload;
      state.page = 1;
    },
    /**
     * Apply an explicit trading-day range, clearing any preset — the API
     * refuses a request carrying both.
     */
    setOrdersDateRange(
      state,
      action: PayloadAction<{ from: string; to: string }>,
    ) {
      state.window = {
        period: "",
        dateFrom: action.payload.from,
        dateTo: action.payload.to,
      };
      state.page = 1;
    },
    /** Apply a preset token, clearing any explicit range for the same reason. */
    setOrdersPeriod(state, action: PayloadAction<string>) {
      state.window = { period: action.payload, dateFrom: "", dateTo: "" };
      state.page = 1;
    },
    /** Open an overlay against a row. */
    openOrderOverlay(
      state,
      action: PayloadAction<{ overlay: OrderOverlay; orderId: string }>,
    ) {
      state.overlay = action.payload.overlay;
      state.activeOrderId = action.payload.orderId;
    },
    closeOrderOverlay(state) {
      state.overlay = null;
      state.activeOrderId = null;
    },
  },
});

export const {
  hydrateOrdersQuery,
  setOrdersListPending,
  setOrders,
  setOrdersPage,
  setOrdersPageSize,
  setOrdersSearch,
  setOrdersStatus,
  setOrdersFilters,
  setOrdersDateRange,
  setOrdersPeriod,
  openOrderOverlay,
  closeOrderOverlay,
} = ordersSlice.actions;

export default ordersSlice.reducer;
